import React, { useEffect, useRef, useState } from 'react';
import MessageBubble from './MessageBubble';
import TypingIndicator from './TypingIndicator';
import Button from '../../../components/ui/Button';


const ChatMessages = ({ 
  messages, 
  isTyping = false, 
  onLoadMore, 
  hasMoreMessages = false,
  isLoading = false 
}) => {
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleScroll = () => {
    if (!messagesContainerRef?.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef?.current;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
    
    setShowScrollToBottom(!isNearBottom);

    // Load more messages when scrolled to top
    if (scrollTop === 0 && hasMoreMessages && !isLoading) {
      onLoadMore?.();
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const container = messagesContainerRef?.current;
    if (container) {
      container?.addEventListener('scroll', handleScroll);
      return () => container?.removeEventListener('scroll', handleScroll);
    }
  }, [hasMoreMessages, isLoading]);

  if (messages?.length === 0 && !isTyping) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center space-y-4 max-w-sm">
          <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto">
            <svg viewBox="0 0 24 24" className="w-8 h-8 text-primary-foreground">
              <path 
                fill="currentColor" 
                d="M12 2L3.09 8.26L4 21L12 17L20 21L20.91 8.26L12 2Z"
              />
              <circle cx="12" cy="10" r="2" fill="var(--color-secondary)" />
            </svg>
          </div>
          <div>
            <h3 className="font-heading font-semibold text-foreground mb-2">
              Olá! Sou o Mentor Dai
            </h3>
            <p className="text-sm text-muted-foreground font-caption">
              Estou aqui para ajudá-lo em sua jornada de aprendizagem. Faça uma pergunta ou escolha uma ação rápida abaixo.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 relative">
      <div 
        ref={messagesContainerRef}
        className="h-full overflow-y-auto p-4 space-y-1"
      >
        {/* Load More Button */}
        {hasMoreMessages && (
          <div className="text-center py-4">
            <Button
              variant="ghost"
              onClick={onLoadMore}
              loading={isLoading}
              iconName="ChevronUp"
              iconPosition="left"
              iconSize={16}
              className="text-sm"
            >
              Carregar mensagens anteriores
            </Button>
          </div>
        )}

        {/* Messages */}
        {messages?.map((message) => (
          <MessageBubble
            key={message?.id}
            message={message}
            isUser={message?.type === 'user'}
          />
        ))}

        {/* Typing Indicator */}
        {isTyping && <TypingIndicator />}

        {/* Scroll anchor */}
        <div ref={messagesEndRef} />
      </div>
      {/* Scroll to Bottom Button */}
      {showScrollToBottom && (
        <div className="absolute bottom-4 right-4">
          <Button
            variant="default"
            onClick={scrollToBottom}
            iconName="ChevronDown"
            iconSize={18}
            className="w-10 h-10 rounded-full shadow-tier-2"
            title="Ir para o final"
          />
        </div>
      )}
      {/* Connection Status Overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-background bg-opacity-50 flex items-center justify-center">
          <div className="bg-card border border-border rounded-lg p-4 shadow-tier-2 flex items-center space-x-3">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-foreground">Conectando com Dai...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatMessages;